// New version notice for the installed admin (and any page that wants it).
//
// Two signals mark an update as ready:
// 1. the service worker: a new worker finished installing while a page is
//    already controlled (registration "updatefound", then "installed"), or a
//    worker is already waiting;
// 2. the live build tag: the deployed index.html carries
//    <meta name="build" content="<commit sha>">; when the tag fetched from the
//    server differs from the one this page was built with, a newer build is out.
// The admin shows a thin "New version ready" bar with Reload. applyUpdate()
// tells a waiting worker to take over, then reloads the page.

export const BUILD_CHECK_MS = 10 * 60 * 1000;

let state = { ready: false, source: null };
let waitingRegistration = null;
// The page was just served, so the first check waits a full interval.
let lastCheckAt = Date.now();
let checking = null;
const listeners = new Set();

const emit = () => listeners.forEach((fn) => {
  try {
    fn(state);
  } catch (e) {
    // a listener failing must not stop the others
  }
});

export const getUpdateState = () => state;

export function subscribeUpdate(fn) {
  if (typeof fn !== "function") return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function markUpdateReady(source = "build", registration = null) {
  if (registration) waitingRegistration = registration;
  if (state.ready) return;
  state = { ready: true, source };
  emit();
}

// The build tag this page was served with ("local" in development, empty when
// the placeholder was never filled).
export function currentBuild(doc = typeof document === "undefined" ? null : document) {
  try {
    const raw = doc?.querySelector?.('meta[name="build"]')?.getAttribute?.("content") ?? "";
    const tag = String(raw).trim();
    return tag && !tag.includes("%") ? tag : "";
  } catch (e) {
    return "";
  }
}

export const shortBuild = (tag) => String(tag ?? "").trim().slice(0, 7);

export function parseBuildTag(html) {
  const text = typeof html === "string" ? html : "";
  const tagMatch = text.match(/<meta[^>]*name=["']build["'][^>]*>/i);
  if (!tagMatch) return "";
  const content = tagMatch[0].match(/content=["']([^"']*)["']/i);
  const tag = String(content?.[1] ?? "").trim();
  return tag && !tag.includes("%") ? tag : "";
}

// Watch a service worker registration. hasController: the page is already
// controlled (so a newly installed worker is an update, not the first install).
export function watchRegistration(registration, hasController) {
  if (!registration) return;
  if (registration.waiting && hasController) markUpdateReady("worker", registration);
  try {
    registration.addEventListener?.("updatefound", () => {
      const worker = registration.installing;
      if (!worker) return;
      worker.addEventListener?.("statechange", () => {
        if (worker.state === "installed" && hasController) markUpdateReady("worker", registration);
      });
    });
  } catch (e) {
    // no events, no banner from the worker; the build check still runs
  }
}

// Fetch the live build tag and compare. At most once per BUILD_CHECK_MS unless
// force. Resolves true when an update is ready.
export async function checkLiveBuild({ force = false, now = Date.now(), fetchImpl, current, url = "/" } = {}) {
  if (state.ready) return true;
  const mine = current ?? currentBuild();
  if (!mine) return false;
  if (!force && lastCheckAt && now - lastCheckAt < BUILD_CHECK_MS) return false;
  if (checking) return checking;
  const doFetch = fetchImpl ?? (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return false;
  lastCheckAt = now;
  checking = (async () => {
    try {
      const sep = url.includes("?") ? "&" : "?";
      const res = await doFetch(`${url}${sep}build-check=${now}`, { cache: "no-store", credentials: "same-origin" });
      if (!res?.ok) return false;
      const live = parseBuildTag(await res.text());
      if (live && live !== mine) {
        markUpdateReady("build");
        return true;
      }
      return false;
    } catch (e) {
      return false;
    } finally {
      checking = null;
    }
  })();
  return checking;
}

// Reload into the new version: a waiting worker is told to take over first
// (the existing controllerchange handler may reload too; one reload wins).
export function applyUpdate({ reload } = {}) {
  try {
    waitingRegistration?.waiting?.postMessage?.({ type: "SKIP_WAITING" });
  } catch (e) {
    // reload anyway
  }
  const doReload = reload ?? (() => window.location.reload());
  doReload();
}

// Tests only.
export function resetUpdateState() {
  state = { ready: false, source: null };
  waitingRegistration = null;
  lastCheckAt = 0;
  checking = null;
  listeners.clear();
}
